import Vue from "vue";
import { mapGetters } from "vuex";

import buttonScComponent from "@/components/child/form/buttonSc.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import filePickerComponent from "@/components/child/form/filepicker.vue";
import { Component, Prop, Watch } from "@/decorators";
import TipoReceitaArquivo from "@/models/enum/tipoReceitaArquivo";
import ReceitaArquivoModel from "@/models/receitaArquivoModel";
import ReceitaArquivoService from "@/services/receitaArquivoService";
import { Getters } from "@/store/store";
import { editDate, editDateWithTime } from "@/utils/common/date";
import { DocumentFileType, ImageFileType } from "@/utils/fileExtensions";

import "../crud/crud.scss";

type ModelWithFile = {
    file: File;
    model?: ReceitaArquivoModel;
};

@Component({
    components: {
        filePickerComponent,
        fieldsetComponent,
        buttonScComponent,
        dateTimePickerComponent,
        comboComponent,
    },
    computed: {
        ...mapGetters(["VALIDAR_PERMISSAO_SOMENTE_CONSULTA"] as Getters),
    },
})
export default class UploadReceitaComponent extends Vue {
    // State computed props
    VALIDAR_PERMISSAO_SOMENTE_CONSULTA: (telaDescricao: string) => boolean;

    private uploadService = new ReceitaArquivoService();

    model = new ReceitaArquivoModel();
    selectedFile: File = null;
    uploadedFiles: ReceitaArquivoModel[] = [];
    filesToUploadOrcamento: { file: File; model: ReceitaArquivoModel }[] = [];
    somenteConsultaVenda = false;
    selectedFilesWithNames: ModelWithFile[] = [];

    tipoReceitaArquivoOptions: Array<Object> = [
        { text: "Receita", value: TipoReceitaArquivo.Receita },
        { text: "Comprovante Financeiro", value: TipoReceitaArquivo.ComprovanteFinanceiro },
        { text: "Autenticação de Receita", value: TipoReceitaArquivo.AutenticacaoReceita },
    ];

    @Prop(Number) vendaId: number;

    get isSingleFileMode() {
        return this.selectedFilesWithNames.length === 0;
    }

    private async onView(fileUpload: ReceitaArquivoModel) {
        await this.uploadService.showFiles([fileUpload]);
    }

    private async onDownload(fileUpload: ReceitaArquivoModel) {
        await this.uploadService.onDownload(fileUpload.id);
    }

    private async onDelete(fileUpload: ReceitaArquivoModel) {
        const response = await this.$showExclusaoQuestion(`${this.$t("__.ts.desejaExcl")}?`);

        if (response) {
            if (fileUpload.id) {
                try {
                    const sucesso = await this.uploadService.delete(fileUpload.id).withLoading().resolveWithoutJSON();
                    if (sucesso) {
                        this.load();
                        this.$showExclusao();
                    }
                } catch {}
            } else {
                this.filesToUploadOrcamento = this.filesToUploadOrcamento.filter(p => p.model != fileUpload);
                this.uploadedFiles = this.uploadedFiles.filter(p => p != fileUpload);
            }
        }
    }

    private async load() {
        try {
            if (this.vendaId) {
                this.uploadedFiles = await this.uploadService
                    .listFiles(this.vendaId)
                    .resolveWithJSON<ReceitaArquivoModel[]>();
            }
        } catch {}
    }

    private clear() {
        this.model = new ReceitaArquivoModel();
        this.model.tipo = TipoReceitaArquivo.Receita;
        this.model.descricao = this.getDescricaoPorTipo(this.model.tipo);
        this.model.vendaId = this.vendaId;

        this.selectedFile = null;
    }

    private getDescricaoPorTipo(tipo: TipoReceitaArquivo | number): string {
        if (tipo == null) {
            return "Receita";
        }

        switch (tipo) {
            case TipoReceitaArquivo.Receita:
                return "Receita";
            case TipoReceitaArquivo.ComprovanteFinanceiro:
                return "Comprovante Financeiro";
            case TipoReceitaArquivo.AutenticacaoReceita:
                return "Autenticacao de Receita";
            default:
                return "Receita";
        }
    }

    private onChangeTipo() {
        this.model.descricao = this.getDescricaoPorTipo(this.model.tipo);
    }

    private onChangeMultipleTipo(item: ModelWithFile) {
        item.model.descricao = this.getDescricaoPorTipo(item.model.tipo);
    }

    private editaDateInclusao(date: Date) {
        return editDateWithTime(date);
    }

    private editaDate(date: Date) {
        return editDate(date);
    }

    @Watch("selectedFile")
    private async onChangeSelectedFile() {
        if (!this.selectedFile) {
            return;
        }

        if (!this.selectedFile.isImage() && !this.selectedFile.isDocument()) {
            const allowedFileTypes = [...Object.keys(DocumentFileType), ...Object.keys(ImageFileType)];
            this.$showError(
                this.$t("__.ts.tipoArquivoInvalido"),
                `${this.$t("__.ts.exemplosTipoArquivoPermitido")} ${allowedFileTypes.join(", ")}`,
            );
            this.selectedFile = null;
            return;
        }

        const maxFileSize = 25 * 1024 * 1024;
        if (this.selectedFile.size > maxFileSize) {
            this.$showError(this.$t("__.ts.arquivoMuitoGrande"), this.$t("__.ts.tamanhoMaximoArquivo25mb"));
            this.selectedFile = null;
            return;
        }

        if (this.vendaId) {
            await this.upload(this.vendaId);
        } else {
            const model = new ReceitaArquivoModel();
            model.updateFrom(this.model);
            model.tipo = this.model.tipo;
            model.dataInclusao = new Date();

            this.filesToUploadOrcamento.push({ file: this.selectedFile, model });
            this.uploadedFiles.push(model);
        }
        this.selectedFile = null;
        this.model.descricao = this.getDescricaoPorTipo(this.model.tipo);
    }

    private async onFilesSelected(files: FileList) {
        if (!files || files.length === 0) {
            return;
        }

        this.selectedFilesWithNames = [];

        if (files.length === 1) {
            this.selectedFile = files[0];
            return;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            if (!file.isImage() && !file.isDocument()) {
                const allowedFileTypes = [...Object.keys(DocumentFileType), ...Object.keys(ImageFileType)];
                this.$showError(
                    `${this.$t("__.ts.tipoArquivoInvalido")} - ${file.name}`,
                    `${this.$t("__.ts.exemplosTipoArquivoPermitido")} ${allowedFileTypes.join(", ")}`,
                );
                this.selectedFilesWithNames = [];
                return;
            }

            const maxFileSize = 25 * 1024 * 1024;
            if (file.size > maxFileSize) {
                this.$showError(
                    `${this.$t("__.ts.arquivoMuitoGrande")} - ${file.name}`,
                    this.$t("__.ts.tamanhoMaximoArquivo25mb"),
                );
                this.selectedFilesWithNames = [];
                return;
            }

            const newModel = new ReceitaArquivoModel();
            newModel.tipo = TipoReceitaArquivo.Receita;
            newModel.descricao = "Receita";
            newModel.dataInclusao = new Date();

            this.selectedFilesWithNames.push({ file, model: newModel });
        }
    }

    private clearFileSelection() {
        this.selectedFilesWithNames = [];
        this.selectedFile = null;
        const filePicker = this.$refs.filePicker as any;
        if (filePicker && filePicker.clear) {
            filePicker.clear();
        }
    }

    private async salvarArquivos() {
        const hasFilesWithoutName = this.selectedFilesWithNames.some(f => !f.model?.descricao);
        if (hasFilesWithoutName) {
            this.$showError("Validação", "Por favor, preencha o nome de todos os arquivos.");
            return;
        }

        if (this.vendaId) {
            const errors: string[] = [];
            for (const item of this.selectedFilesWithNames) {
                try {
                    item.model.vendaId = this.vendaId;

                    const sucesso = await this.insertFile(item.model, item.file);
                    if (!sucesso) {
                        errors.push(item.file.name);
                    }
                } catch (error) {
                    errors.push(item.file.name);
                }
            }

            this.clearFileSelection();
            await this.load();

            if (errors.length > 0) {
                this.$showError(
                    "Alguns arquivos não foram enviados",
                    `Os seguintes arquivos falharam: ${errors.join(", ")}`,
                );
            } else {
                this.$showSuccess("Sucesso", "Arquivos enviados com sucesso!");
            }
        } else {
            for (const item of this.selectedFilesWithNames) {
                if (item.model) {
                    const existing = this.filesToUploadOrcamento.find(p => p.model === item.model);
                    if (!existing) {
                        this.filesToUploadOrcamento.push({ file: item.file, model: item.model });
                    }
                    if (!this.uploadedFiles.includes(item.model)) {
                        this.uploadedFiles.push(item.model);
                    }
                }
            }
            this.selectedFilesWithNames = [];
            const filePicker = this.$refs.filePicker as any;
            if (filePicker && filePicker.clear) {
                filePicker.clear();
            }
        }
    }

    public clearAll() {
        if (!this.vendaId && this.selectedFilesWithNames.length > 1) {
            for (const item of this.selectedFilesWithNames) {
                if (item.model) {
                    const existing = this.filesToUploadOrcamento.find(p => p.model === item.model);
                    if (!existing) {
                        this.filesToUploadOrcamento.push({ file: item.file, model: item.model });
                    }
                    if (!this.uploadedFiles.includes(item.model)) {
                        this.uploadedFiles.push(item.model);
                    }
                }
            }
        }
        this.clearFileSelection();
    }

    public async upload(vendaId: number) {
        if (!this.selectedFile && this.filesToUploadOrcamento.length == 0) {
            return null;
        }
        if (this.filesToUploadOrcamento.length > 0) {
            for await (const file of this.filesToUploadOrcamento) {
                file.model.vendaId = vendaId;
                await this.insertFile(file.model, file.file);
            }
            this.filesToUploadOrcamento = [];
            this.uploadedFiles = this.uploadedFiles.filter(f => f.id); // Limpar apenas os temporários
            await this.load();
        } else {
            this.model.vendaId = vendaId;

            const sucesso = await this.insertFile(this.model, this.selectedFile);
            if (sucesso) {
                this.clear();
                await this.load();
            }
        }
    }

    private async insertFile(model: ReceitaArquivoModel, file: File) {
        try {
            await this.uploadService.insertFile(model, file).withLoading().resolveWithoutJSON();

            return true;
        } catch {}
        return false;
    }

    private mounted() {
        this.somenteConsultaVenda = this.VALIDAR_PERMISSAO_SOMENTE_CONSULTA("vendas");

        Promise.all([this.load()])
            .withLoading()
            .then(() => {
                this.clear();
            })
            .catch(() => {});
    }
}
